import { where } from 'firebase/firestore'
import { isEqual } from 'lodash'
import { ILeadLookupConfig, ILeadLookupValue, IWorkspace } from 'types'
import {
  BatchCreateRow,
  BatchDeleteRow,
  BatchRow,
  BatchUpdateRow,
  batchWrite,
  createDoc,
  createTimestamp,
  updateDoc,
  useDoc,
  useDocs,
} from 'utils'

export function cleanDuplicates(rawDbData: Record<string, string>[], dbInputProp: string) {
  const inputValues = rawDbData.map(session => session[dbInputProp])
  const cleanedData = rawDbData.filter(
    (session, index) => index === inputValues.indexOf(session[dbInputProp])
  )
  return { cleanedData, duplicatesNum: rawDbData.length - cleanedData.length }
}

export function isSmartColumnInput(propsToDelete: string[], workspaceId: string) {
  const [content] = useDocs('leadLookupConfig', [where('workspaceId', '==', workspaceId)])
  const isInput = content.some(
    (session: ILeadLookupConfig) =>
      propsToDelete.includes(session.inputProperty!) && session.status == 'success'
  )
  return isInput
}

export function getSmartColumnsWithCharts(configId: string) {
  const [content] = useDoc('leadLookupConfig', configId)
  const chartColumns = content?.outputProperties
    ?.filter(prop => prop.hasChart)
    .map(prop => prop.name)
  return chartColumns
}

export function useWorkspaceSmartColumns(workspaceId: string) {
  const [content] = useDocs('leadLookupConfig', [where('workspaceId', '==', workspaceId)])
  const smartColumnsNames = [
    ...new Set(
      content.flatMap((session: ILeadLookupConfig) =>
        (session.outputProperties || []).map(prop => prop.name)
      )
    ),
  ]
  return smartColumnsNames
}

export async function createNewSmartColumn(userId: string, workspace: IWorkspace) {
  const { id } = await createDoc(userId, 'leadLookupConfig', {
    status: 'intialized',
    workspaceId: workspace.id,
  })
  updateConfigOnWorkspace(userId, workspace, id)
}

export async function updateLookUpConfig(
  userId: string,
  lookupId: string,
  newConfig: Partial<ILeadLookupConfig>
) {
  updateDoc(userId, 'leadLookupConfig', {
    id: lookupId,
    ...newConfig,
  })
}

export async function updateConfigOnWorkspace(
  userId: string,
  workspace: IWorkspace,
  lookupId: string
) {
  const existingConfig = { ...workspace?.smartColumnsConfig }
  const lookUpIds = [...(existingConfig.lookUpIds || []), lookupId]
  await updateDoc(userId, 'Workspace', {
    id: workspace?.id!,
    smartColumnsConfig: { ...existingConfig, updated: createTimestamp(), lookUpIds: lookUpIds },
  })
}

export async function removeConfigFromWorkspace(
  userId: string,
  workspace: IWorkspace,
  lookupId: string
) {
  const newConfig = { ...workspace?.smartColumnsConfig }
  const lookUpIds = (newConfig.lookUpIds || []).filter(id => id !== lookupId)
  await updateDoc(userId, 'Workspace', {
    id: workspace?.id!,
    smartColumnsConfig: { ...newConfig, updated: createTimestamp(), lookUpIds: lookUpIds },
  })
}

export function parseNewLookupValues(
  rawDbData: Record<string, string>[],
  inputProp: string,
  dbInputProp: string,
  outputProps: string[],
  dbOutputProps: string[],
  configId: string,
  workspaceId: string
) {
  const batchRows = rawDbData.map(
    session =>
      ({
        type: 'create',
        body: {
          configId: configId,
          inputName: inputProp,
          inputValue: session[dbInputProp],
          outputProperties: [
            ...outputProps.map((prop, index) => ({
              name: prop,
              value: session[dbOutputProps[index]],
            })),
          ],
          workspaceId: workspaceId,
        },
      } as BatchCreateRow<'leadLookupValues'>)
  )
  return batchRows
}

export function parseLookupValuesForUpdate(
  rawDbData: Record<string, string>[],
  inputProp: string,
  dbInputProp: string,
  outputProps: string[],
  dbOutputProps: string[],
  configId: string,
  workspaceId: string,
  content: ILeadLookupValue[]
) {
  const comparisionMap = new Map(content.map(doc => [doc.inputValue, doc]))
  const rawInputValues = new Set(rawDbData.map(session => session[dbInputProp]))

  const creates: BatchCreateRow<'leadLookupValues'>[] = []
  const updates: BatchUpdateRow<'leadLookupValues'>[] = []
  const deletes: BatchDeleteRow[] = []

  for (const session of rawDbData) {
    const inputValue = session[dbInputProp]
    const newOutputProperties = outputProps.map((prop, index) => ({
      name: prop,
      value: session[dbOutputProps[index]],
    }))
    const existingDoc = comparisionMap.get(inputValue)

    if (!existingDoc) {
      creates.push({
        type: 'create',
        body: {
          configId,
          inputName: inputProp,
          inputValue,
          outputProperties: newOutputProperties,
          workspaceId: workspaceId,
        },
      })
    } else {
      const existingOutputProperties = existingDoc.outputProperties || []
      if (!isEqual(existingOutputProperties, newOutputProperties)) {
        updates.push({
          type: 'update',
          body: {
            id: existingDoc.id ?? session.id,
            configId,
            inputName: inputProp,
            inputValue,
            outputProperties: newOutputProperties,
          },
        })
      }
    }
  }

  for (const doc of content) {
    if (!rawInputValues.has(doc.inputValue)) {
      deletes.push({
        type: 'delete',
        body: { id: doc.id },
      })
    }
  }

  const batchRows = [...creates, ...updates, ...deletes]

  return {
    batchRows,
    newRows: creates.length,
    updatedRows: updates.length,
    deletedRows: deletes.length,
  }
}

export async function updateLookupValues(
  userId: string,
  batchRows: BatchRow<'leadLookupValues'>[],
  setProgress: (progress: number) => void,
  setError: (error: string) => void
) {
  try {
    await batchWrite(userId, 'leadLookupValues', batchRows, setProgress)
  } catch (error) {
    console.error(error)
    setProgress(0)
    setError('Error Updating Lookup Values')
  }
}
